import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { createBulkIssuanceApi, getBulkIssuanceMetaApi, getUniformItemsApi } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import { useSelector } from 'react-redux';
import Breadcrumbs from 'components/Breadcrumbs';

const BulkIssuance = () => {
  const navigate = useNavigate();
  const { warehouseOptions, warehousesLoading } = useUniformMgmt();
  const { userData, position: positions, userDataLoading } = useSelector((state) => state.common);

  const [loading, setLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [uniformItemsLoading, setUniformItemsLoading] = useState(false);
  const [uniformItems, setUniformItems] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [issuanceDate, setIssuanceDate] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState(null);

  const positionOptions = (positions || []).map(p => ({ label: p.name, value: p.id }));

  // Reset dependent fields whenever position is cleared.
  useEffect(() => {
    if (selectedPosition) return;
    setEmployeeOptions([]);
    setUniformItems([]);
    setSelectedEmployees([]);
    setSelectedItem(null);
  }, [selectedPosition]);

  // Position filter active -> fetch employees & uniforms scoped to that position
  useEffect(() => {
    if (!selectedPosition) return;
    setMetaLoading(true);
    setSelectedEmployees([]);
    setSelectedItem(null);
    getBulkIssuanceMetaApi({ position: selectedPosition.value }).then(response => {
      const data = response.data?.data || {};
      setEmployeeOptions(
        (data.employees || [])
          .slice()
          .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' }))
          .map(e => ({
            label: `${e.first_name} ${e.last_name}${e.emp_no ? ` (${e.emp_no})` : ''}`,
            value: e.id, emp_no: e.emp_no || ''
          }))
      );
      setUniformItems(
        (data.uniform_items || [])
          .map(i => ({ label: i.name, value: i.id }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      );
    }).catch(() => {
      setEmployeeOptions([]);
      setUniformItems([]);
    }).finally(() => setMetaLoading(false));
  }, [selectedPosition]);

  const handleSubmit = () => {
    if (!selectedPosition) {
      toast.error('Please select a position first', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    if (!selectedEmployees.length || !selectedItem || !selectedWarehouse || !issuanceDate) {
      toast.error('Please fill all required fields', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    setLoading(true);
    setResult(null);
    createBulkIssuanceApi({
      company_user_ids: selectedEmployees.map(e => e.value),
      uniform_item: selectedItem.value,
      warehouse: selectedWarehouse.value,
      quantity,
      issuance_date: issuanceDate,
      notes
    }).then(response => {
      setLoading(false);
      setResult(response.data);
      toast.success(response.data?.message || 'Bulk issuance completed', { position: "top-right", autoClose: 3000, transition: Bounce });
    }).catch(e => {
      setLoading(false);
      const data = e.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        data?.non_field_errors?.[0] ||
        (data && typeof data === 'object'
          ? Object.entries(data)
              .filter(([k]) => k !== 'status')
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join(' | ')
          : null) ||
        'Bulk issuance failed';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const renderEmployee = (_, row) => (
    <div>
      <div>{row.name || '—'}</div>
      {row.emp_no && <small className="text-muted">{row.emp_no}</small>}
    </div>
  );

  const errorColumns = [
    { title: 'Employee', key: 'employee', width: '45%', render: renderEmployee },
    {
      title: 'Error', dataIndex: 'error', width: '55%',
      render: (err) => <span className="text-danger" style={{ wordBreak: 'break-word' }}>{err}</span>
    }
  ];

  const issuedColumns = [
    { title: 'Employee', key: 'employee', render: renderEmployee }
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs maintitle="Bulk Issuance" />
<div className="row">
          <div className="col-md-8">
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label className="col-form-label">
                      Position <span className="text-danger">*</span>
                      <small className="text-muted ms-2"></small>
                    </label>
                    <FilterSelect isClearable onChange={setSelectedPosition} optionLabel="label" optionValue="value"
                      options={positionOptions} value={selectedPosition}
                      placeholder="Select a position to begin" isLoading={false} />
                  </div>
                  <div className="col-md-12 mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <label className="col-form-label mb-0">Employees <span className="text-danger">*</span></label>
                      <div>
                        <button type="button" className="btn btn-link btn-sm p-0 me-3"
                          disabled={!selectedPosition || metaLoading || employeeOptions.length === 0 || selectedEmployees.length === employeeOptions.length}
                          onClick={() => setSelectedEmployees(employeeOptions)}>
                          Select All ({employeeOptions.length})
                        </button>
                        <button type="button" className="btn btn-link btn-sm p-0 text-danger"
                          disabled={selectedEmployees.length === 0}
                          onClick={() => setSelectedEmployees([])}>
                          Clear
                        </button>
                      </div>
                    </div>
                    <FilterSelect isClearable isMulti onChange={setSelectedEmployees} optionLabel="label" optionValue="value"
                      options={employeeOptions} value={selectedEmployees}
                      placeholder={!selectedPosition ? "Select a position first" : "Select Employees"}
                      isDisabled={!selectedPosition}
                      isLoading={metaLoading} />
                    {selectedEmployees.length > 0 && (
                      <small className="text-muted">{selectedEmployees.length} of {employeeOptions.length} employee(s) selected</small>
                    )}
                    {selectedPosition && !metaLoading && employeeOptions.length === 0 && (
                      <small className="text-warning d-block">No employees in this position.</small>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="col-form-label">Uniform Item <span className="text-danger">*</span></label>
                    <FilterSelect isClearable onChange={setSelectedItem} optionLabel="label" optionValue="value"
                      options={uniformItems} value={selectedItem}
                      placeholder={!selectedPosition ? "Select a position first" : "Select Item"}
                      isDisabled={!selectedPosition}
                      isLoading={metaLoading} />
                    {selectedPosition && !metaLoading && uniformItems.length === 0 && (
                      <small className="text-warning d-block">No uniforms mapped to this position.</small>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="col-form-label">Warehouse <span className="text-danger">*</span></label>
                    <FilterSelect isClearable onChange={setSelectedWarehouse} optionLabel="label" optionValue="value"
                      options={warehouseOptions} value={selectedWarehouse} placeholder="Select Warehouse" isLoading={warehousesLoading} />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="col-form-label">Qty per Employee</label>
                    <input type="number" className="form-control" min="1" value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="col-form-label">Issuance Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={issuanceDate}
                      onChange={(e) => setIssuanceDate(e.target.value)} />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="col-form-label">Notes</label>
                    <input type="text" className="form-control" value={notes}
                      onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-2">
                  <Button loading={loading} disabled={loading || !selectedPosition} className="btn btn-primary"
                    onClick={handleSubmit} label="Issue to All" />
                </div>
          </div>

          {result && (
            <div className="col-md-4 mt-3 mt-md-0">
              <div className="card mb-3" style={{ overflow: 'hidden' }}>
                <div className="card-header d-flex align-items-center justify-content-between"
                  style={{ background: '#f6ffed', borderBottom: '1px solid #b7eb8f' }}>
                  <h5 className="mb-0 text-success">
                    <i className="fa fa-check-circle me-2" />Result
                  </h5>
                  <Tag color="green" className="mb-0">
                    {result.issued?.length || 0} issued
                  </Tag>
                </div>
                <div className="card-body">
                  <p className="mb-3">{result.message}</p>
                  {result.issued?.length > 0 && (
                    <Table columns={issuedColumns} dataSource={result.issued} rowKey={(r, i) => i}
                      pagination={false} size="small" scroll={{ x: 'max-content' }} />
                  )}
                </div>
              </div>
              {result.errors?.length > 0 && (
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div className="card-header d-flex align-items-center justify-content-between"
                    style={{ background: '#fff1f0', borderBottom: '1px solid #ffa39e' }}>
                    <h5 className="mb-0 text-danger">
                      <i className="fa fa-exclamation-triangle me-2" />Errors
                    </h5>
                    <Tag color="red" className="mb-0">{result.errors.length}</Tag>
                  </div>
                  <div className="card-body p-0">
                    <Table columns={errorColumns} dataSource={result.errors} rowKey={(r, i) => i}
                      pagination={false} size="small" scroll={{ x: 'max-content' }} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkIssuance;
